import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Select from '../../../components/ui/Select';
import { XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, LineChart, Line, PieChart, Pie, Cell } from 'recharts';

const CampaignAnalytics = () => {
  const [timeRange, setTimeRange] = useState('7d');
  const [selectedMetric, setSelectedMetric] = useState('opens');

  const timeRangeOptions = [
  { value: '24h', label: 'Last 24 Hours' },
  { value: '7d', label: 'Last 7 Days' },
  { value: '30d', label: 'Last 30 Days' },
  { value: '90d', label: 'Last 90 Days' }];


  const metricOptions = [
  { value: 'opens', label: 'Email Opens' },
  { value: 'clicks', label: 'Link Clicks' },
  { value: 'replies', label: 'Replies' },
  { value: 'bounces', label: 'Bounces' }];


  const campaignStats = {
    totalSent: 1247,
    delivered: 1198,
    opened: 856,
    clicked: 234,
    replied: 67,
    bounced: 49,
    unsubscribed: 12
  };

  const performanceData = [
  { date: '2024-10-25', opens: 45, clicks: 12, replies: 3, bounces: 2 },
  { date: '2024-10-26', opens: 67, clicks: 18, replies: 5, bounces: 1 },
  { date: '2024-10-27', opens: 89, clicks: 25, replies: 8, bounces: 3 },
  { date: '2024-10-28', opens: 123, clicks: 34, replies: 12, bounces: 2 },
  { date: '2024-10-29', opens: 156, clicks: 42, replies: 15, bounces: 4 },
  { date: '2024-10-30', opens: 198, clicks: 56, replies: 18, bounces: 1 },
  { date: '2024-10-31', opens: 178, clicks: 47, replies: 6, bounces: 2 }];


  const deviceData = [
  { name: 'Desktop', value: 45, color: '#1E3A5F' },
  { name: 'Mobile', value: 35, color: '#4A90A4' },
  { name: 'Tablet', value: 20, color: '#E67E22' }];


  const topPerformers = [
  {
    id: 1,
    name: "Sarah Mitchell",
    outlet: "Literary Review Weekly",
    avatar: "https://images.unsplash.com/photo-1637562772116-e01cda44fce8",
    avatarAlt: "Professional headshot of woman with shoulder-length brown hair in navy blazer",
    openTime: "2 hours ago",
    clicked: true,
    replied: true
  },
  {
    id: 2,
    name: "Marcus Rodriguez",
    outlet: "The Literary Times",
    avatar: "https://images.unsplash.com/photo-1678282955795-200c1e18bc7d",
    avatarAlt: "Professional headshot of Hispanic man with short black hair in dark suit",
    openTime: "4 hours ago",
    clicked: true,
    replied: false
  },
  {
    id: 3,
    name: "Emily Chen",
    outlet: "Book Blog Central",
    avatar: "https://images.unsplash.com/photo-1560859389-c4fb2bd88016",
    avatarAlt: "Professional headshot of Asian woman with long black hair in white blouse",
    openTime: "6 hours ago",
    clicked: false,
    replied: true
  }];


  const calculateRate = (numerator, denominator) => {
    return denominator > 0 ? (numerator / denominator * 100)?.toFixed(1) : '0.0';
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-3">
          <div className="w-8 h-8 bg-primary rounded-md flex items-center justify-center">
            <Icon name="BarChart3" size={16} color="white" />
          </div>
          <h2 className="text-lg font-heading font-semibold text-foreground">Campaign Analytics</h2>
        </div>
        <div className="flex items-center space-x-3">
          <Select
            options={timeRangeOptions}
            value={timeRange}
            onChange={setTimeRange} />

          <Button variant="outline" iconName="Download" iconPosition="left">
            Export
          </Button>
        </div>
      </div>
      {/* Key Metrics */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-4">
        <div className="bg-card border border-border rounded-lg p-4">
          <div className="flex items-center space-x-2 mb-2">
            <Icon name="Send" size={16} className="text-muted-foreground" />
            <span className="text-sm font-medium text-muted-foreground">Sent</span>
          </div>
          <div className="text-2xl font-bold text-foreground">{campaignStats?.totalSent?.toLocaleString()}</div>
        </div>

        <div className="bg-card border border-border rounded-lg p-4">
          <div className="flex items-center space-x-2 mb-2">
            <Icon name="CheckCircle" size={16} className="text-success" />
            <span className="text-sm font-medium text-muted-foreground">Delivered</span>
          </div>
          <div className="text-2xl font-bold text-foreground">{campaignStats?.delivered?.toLocaleString()}</div>
          <div className="text-xs text-success">
            {calculateRate(campaignStats?.delivered, campaignStats?.totalSent)}%
          </div>
        </div>

        <div className="bg-card border border-border rounded-lg p-4">
          <div className="flex items-center space-x-2 mb-2">
            <Icon name="Eye" size={16} className="text-accent" />
            <span className="text-sm font-medium text-muted-foreground">Opened</span>
          </div>
          <div className="text-2xl font-bold text-foreground">{campaignStats?.opened?.toLocaleString()}</div>
          <div className="text-xs text-accent">
            {calculateRate(campaignStats?.opened, campaignStats?.delivered)}%
          </div>
        </div>

        <div className="bg-card border border-border rounded-lg p-4">
          <div className="flex items-center space-x-2 mb-2">
            <Icon name="MousePointer" size={16} className="text-secondary" />
            <span className="text-sm font-medium text-muted-foreground">Clicked</span>
          </div>
          <div className="text-2xl font-bold text-foreground">{campaignStats?.clicked?.toLocaleString()}</div>
          <div className="text-xs text-secondary">
            {calculateRate(campaignStats?.clicked, campaignStats?.opened)}%
          </div>
        </div>

        <div className="bg-card border border-border rounded-lg p-4">
          <div className="flex items-center space-x-2 mb-2">
            <Icon name="MessageCircle" size={16} className="text-success" />
            <span className="text-sm font-medium text-muted-foreground">Replied</span>
          </div>
          <div className="text-2xl font-bold text-foreground">{campaignStats?.replied?.toLocaleString()}</div>
          <div className="text-xs text-success">
            {calculateRate(campaignStats?.replied, campaignStats?.opened)}%
          </div>
        </div>

        <div className="bg-card border border-border rounded-lg p-4">
          <div className="flex items-center space-x-2 mb-2">
            <Icon name="AlertTriangle" size={16} className="text-error" />
            <span className="text-sm font-medium text-muted-foreground">Bounced</span>
          </div>
          <div className="text-2xl font-bold text-foreground">{campaignStats?.bounced?.toLocaleString()}</div>
          <div className="text-xs text-error">
            {calculateRate(campaignStats?.bounced, campaignStats?.totalSent)}%
          </div>
        </div>

        <div className="bg-card border border-border rounded-lg p-4">
          <div className="flex items-center space-x-2 mb-2">
            <Icon name="UserMinus" size={16} className="text-warning" />
            <span className="text-sm font-medium text-muted-foreground">Unsubscribed</span>
          </div>
          <div className="text-2xl font-bold text-foreground">{campaignStats?.unsubscribed?.toLocaleString()}</div>
          <div className="text-xs text-warning">
            {calculateRate(campaignStats?.unsubscribed, campaignStats?.totalSent)}%
          </div>
        </div>
      </div>
      {/* Charts Section */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Performance Trend */}
        <div className="lg:col-span-2 bg-card border border-border rounded-lg p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-medium text-foreground">Performance Trend</h3>
            <Select
              options={metricOptions}
              value={selectedMetric}
              onChange={setSelectedMetric} />

          </div>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={performanceData}>
                <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" />
                <XAxis
                  dataKey="date"
                  stroke="var(--color-muted-foreground)"
                  fontSize={12}
                  tickFormatter={(value) => new Date(value)?.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} />

                <YAxis stroke="var(--color-muted-foreground)" fontSize={12} />
                <Tooltip
                  contentStyle={{
                    backgroundColor: 'var(--color-card)',
                    border: '1px solid var(--color-border)',
                    borderRadius: '6px'
                  }} />

                <Line
                  type="monotone"
                  dataKey={selectedMetric}
                  stroke="var(--color-primary)"
                  strokeWidth={2}
                  dot={{ fill: 'var(--color-primary)', strokeWidth: 2, r: 4 }} />

              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Device Breakdown */}
        <div className="bg-card border border-border rounded-lg p-6">
          <h3 className="text-lg font-medium text-foreground mb-4">Device Breakdown</h3>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={deviceData}
                  cx="50%"
                  cy="50%"
                  innerRadius={40}
                  outerRadius={80}
                  paddingAngle={5}
                  dataKey="value">

                  {deviceData?.map((entry, index) =>
                  <Cell key={`cell-${index}`} fill={entry?.color} />
                  )}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          </div>
          <div className="space-y-2 mt-4">
            {deviceData?.map((device, index) =>
            <div key={index} className="flex items-center justify-between">
                <div className="flex items-center space-x-2">
                  <div
                  className="w-3 h-3 rounded-full"
                  style={{ backgroundColor: device?.color }} />

                  <span className="text-sm text-foreground">{device?.name}</span>
                </div>
                <span className="text-sm font-medium text-foreground">{device?.value}%</span>
              </div>
            )}
          </div>
        </div>
      </div>
      {/* Top Performers */}
      <div className="bg-card border border-border rounded-lg p-6">
        <h3 className="text-lg font-medium text-foreground mb-4">Top Performing Recipients</h3>
        <div className="space-y-4">
          {topPerformers?.map((performer) =>
          <div key={performer?.id} className="flex items-center justify-between p-4 border border-border rounded-lg">
              <div className="flex items-center space-x-3">
                <img
                src={performer?.avatar}
                alt={performer?.avatarAlt}
                className="w-10 h-10 rounded-full object-cover" />

                <div>
                  <div className="font-medium text-foreground">{performer?.name}</div>
                  <div className="text-sm text-muted-foreground">{performer?.outlet}</div>
                  <div className="text-xs text-muted-foreground">Opened {performer?.openTime}</div>
                </div>
              </div>
              <div className="flex items-center space-x-4">
                <div className="flex items-center space-x-2">
                  <Icon
                  name="MousePointer"
                  size={14}
                  className={performer?.clicked ? 'text-success' : 'text-muted-foreground'} />

                  <span className="text-xs text-muted-foreground">Clicked</span>
                </div>
                <div className="flex items-center space-x-2">
                  <Icon
                  name="MessageCircle"
                  size={14}
                  className={performer?.replied ? 'text-success' : 'text-muted-foreground'} />

                  <span className="text-xs text-muted-foreground">Replied</span>
                </div>
                <Button variant="ghost" size="sm" iconName="ExternalLink" iconPosition="left">
                  View
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>);

};

export default CampaignAnalytics;